/**
 * The results — the *output* of a query, never the entry point (D7).
 *
 * Two presentations of one query model. On a phone each result is a card whose
 * largest number is the attribute the list is sorted by: if you asked for
 * "lightest first", the weight is the thing you can read at arm's length, and
 * the rest is supporting detail. On a wide screen the same rows become a dense
 * table, because that is what a wide screen is for.
 *
 * Neither presentation ever prints a number it does not have (REQ-WEB-007).
 */
import { Component } from "preact";
import type { ComponentChild } from "preact";
import { useCallback, useMemo, useState } from "preact/hooks";

import type { Attribute, Cell, LensRow, Range } from "./api";
import { toDisplay } from "./api";
import {
  focalSummary, formatCell, formatCellWithUnit, formatNumber, hasNoCitation, lensName, UNKNOWN,
} from "./format";
import { AgreementMark } from "./agreement";
import { humaniseComputed } from "./formula";
import { useFocusSource, CellProvenance } from "./provenance";
import type { Query } from "./query";
import { IDENTITY_KEYS, labelParts, shortLabel } from "./query";

interface Props {
  rows: LensRow[];
  query: Query;
  byKey: Map<string, Attribute>;
  ranges: Map<string, Range>;
  /** Navigate to a lens. A real href, so middle-click and share still work. */
  onOpen: (slug: string) => void;
  /** Which lenses are ticked for comparison (REQ-WEB-027). */
  selected: Set<string>;
  onToggleSelect: (slug: string) => void;
  /**
   * True for a row whose image circle is smaller than the chosen sensor
   * (REQ-WEB-026: "lenses shown under that opt-in are visibly marked").
   * Absent when the opt-in is off, because then no such row is present.
   */
  cropsBelow?: ((row: LensRow) => boolean) | undefined;
  /** The row's mount name, present only when the list spans several mounts (#211). */
  mountOf?: ((row: LensRow) => string) | undefined;
  /**
   * The camera, as the query a lens link carries ("?mount=…&format=…"), so the
   * lens page judges its category on the same camera as the card (REQ-WEB-035).
   */
  lensSearch?: string | undefined;
}

/** The mark itself, so the card and the table cannot drift apart. */
function CropsBadge() {
  return (
    // No hover text (#213): the note under the list says it in full ("They fit,
    // but the camera crops to that area").
    <span class="crops-badge">Crops image</span>
  );
}

/**
 * A result is a link (REQ-WEB-023).
 *
 * Deliberately an <a> with a real href rather than a click handler on a div:
 * that is what makes long-press-to-share, open-in-new-tab and the browser's own
 * link affordances work. The handler only intercepts the plain left click so
 * navigation stays client-side; modified clicks fall through to the browser.
 */
function openable(slug: string, onOpen: (slug: string) => void, search = "") {
  return {
    href: `/lens/${slug}${search}`,
    onClick: (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      onOpen(slug);
    },
  };
}

/**
 * A click anywhere on a desktop row opens the lens (REQ-WEB-023), as the whole
 * card does on a phone. What already does something of its own keeps doing it:
 * the name link, the compare checkbox, a value's citation button, and anything
 * inside an open citation. So does finishing a text selection, because people
 * copy model names and numbers out of this table. The name link stays the
 * keyboard route; the row itself takes no focus.
 */
function openFromRow(e: MouseEvent, slug: string, onOpen: (slug: string) => void) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  const target = e.target as Element | null;
  if (target?.closest("a, button, input, label, select, textarea, summary, .cell-prov")) return;
  if (window.getSelection()?.toString()) return;
  onOpen(slug);
}

/**
 * The headline number: the attribute the list is ordered by.
 *
 * It carries its citation too. This is the number people actually read — D7
 * makes it the largest thing on the card — so leaving provenance off it while
 * the smaller specs had it got the priority exactly backwards. Found by trying
 * to photograph a conflict on the sorted column and finding no marker at all.
 */
function Headline(props: {
  row: LensRow;
  query: Query;
  byKey: Map<string, Attribute>;
  onOpenProvenance: () => void;
  open: boolean;
}) {
  const { row, query, byKey } = props;
  const attr = byKey.get(query.sortKey);
  const cell = row.values[query.sortKey];
  const text = formatCellWithUnit(attr, cell);
  // Before the early returns: a hook must run on every render (REQ-WEB-005,
  // web-050 C7: focusing the headline number names its source too).
  const focus = useFocusSource(cell?.source_id ?? null, text === UNKNOWN);
  if (IDENTITY_KEYS.has(query.sortKey)) return null;
  const competing = cell?.competing_values ?? 0;
  const distinct = cell?.distinct_values ?? competing;
  const publishers = cell?.distinct_publishers ?? competing;
  const agree = cell?.values_agree;

  if (cell?.computed) {
    return (
      <div class={`headline headline-computed${text === UNKNOWN ? " headline-unknown" : ""}`}>
        <span class="headline-value">{text}</span>
        <span class="headline-label">{humaniseComputed(query.sortKey)}</span>
      </div>
    );
  }

  return (
    <div class="headline-wrap">
      <button
        type="button"
        class={`headline${text === UNKNOWN ? " headline-unknown" : ""}${
          props.open ? " headline-open" : ""
        }`}
        aria-expanded={props.open}
        {...focus.focusProps}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.onOpenProvenance();
        }}
      >
        <span class="headline-value">
          {text}
          <AgreementMark competing={competing} distinct={distinct} publishers={publishers} agree={agree} />
        </span>
        <span class="headline-label">{attr?.label ?? humaniseComputed(query.sortKey)}</span>
      </button>
      {focus.captionEl}
    </div>
  );
}

/** How far along its own range this value sits — a glanceable sense of scale. */
function Bar(props: { attr: Attribute | undefined; cell: Cell | undefined; range: Range | undefined }) {
  const { attr, cell, range } = props;
  if (!attr || !cell || typeof cell.value !== "number") return null;
  if (!range || range.min === null || range.max === null || range.max === range.min) return null;
  const pct = ((cell.value - range.min) / (range.max - range.min)) * 100;
  return (
    <div class="bar" aria-hidden="true">
      <div class="bar-fill" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
    </div>
  );
}

/**
 * One spec, and its citation one tap away (REQ-WEB-005).
 *
 * A button rather than a hover target: phones have no hover, and the whole
 * point of this project is that the number is traceable by whoever is reading
 * it, not only by someone with a mouse.
 */
function Spec(props: {
  attr: Attribute | undefined;
  k: string;
  cell: Cell | undefined;
  slug: string;
}) {
  const [open, setOpen] = useState(false);
  const text = formatCellWithUnit(props.attr, props.cell);
  const unknown = text === UNKNOWN;
  const competing = props.cell?.competing_values ?? 0;
  const distinct = props.cell?.distinct_values ?? competing;
  const publishers = props.cell?.distinct_publishers ?? competing;
  const agree = props.cell?.values_agree;
  const focus = useFocusSource(props.cell?.source_id ?? null, unknown);

  // A computed value is not a claim anyone made, so there is no citation to
  // open. Rendering the button anyway would promise a provenance panel and
  // then ask the API for the sources of a column it has never heard of. A spec
  // that a rule says cannot apply has nothing to cite either (REQ-WEB-007); one a
  // source says does not apply to this lens is cited like any value (D45).
  if (hasNoCitation(props.cell)) {
    return (
      <div class={`spec spec-computed${unknown ? " spec-unknown" : ""}${props.cell?.state === "not_applicable" ? " spec-na" : ""}`}>
        <dt>{shortLabel(props.attr, props.k)}</dt>
        <dd>{text}</dd>
      </div>
    );
  }

  return (
    // A term and its values directly inside the group, as a description list
    // requires (REQ-WEB-009). The button used to wrap both, which axe reports
    // as a broken list on every card (#216). It now sits in the value, as a
    // transparent box laid over the whole pair, so the pair is still the
    // target and the button's own box is at least 44 px (REQ-WEB-005, -017).
    <div class={`spec spec-tappable${unknown ? " spec-unknown" : ""}${props.cell?.state === "not_applicable" ? " spec-na" : ""}${open ? " spec-open" : ""}`}>
      <dt>
        {shortLabel(props.attr, props.k)}
        <AgreementMark competing={competing} distinct={distinct} publishers={publishers} agree={agree} />
      </dt>
      <dd>
        {/* The value as seen; the button below is the same value as heard,
            and a transparent box over the whole pair as touched. */}
        <span class="spec-value" aria-hidden="true">{text}</span>
        <button
          type="button"
          class="spec-button"
          aria-label={text}
          aria-expanded={open}
          {...focus.focusProps}
          onClick={(e) => {
            // The spec sits inside the card's link; opening a citation must not
            // also navigate to the lens.
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
        />
        {focus.captionEl}
      </dd>
      {open ? (
        <dd class="spec-prov">
          <CellProvenance
            slug={props.slug}
            attributeKey={props.k}
            attr={props.attr}
            sourceId={props.cell?.source_id ?? null}
            competing={competing}
            distinct={distinct}
            publishers={publishers}
            agree={agree}
            unknown={unknown}
            notApplicable={props.cell?.state === "not_applicable"}
          />
        </dd>
      ) : null}
    </div>
  );
}

/** Sorting by a spec that no visible lens states.
 *
 * The list is correct and the order is arbitrary, which looks identical to a
 * bug: a column of dashes with a "SORT: Weight" control above it. Saying so
 * costs one line and turns a apparent fault into a fact about the data
 * (REQ-DM-015 — sparse coverage is reported honestly, not hidden).
 */
function SortEmptyNote(props: {
  rows: LensRow[];
  query: Query;
  byKey: Map<string, Attribute>;
}) {
  const { rows, query, byKey } = props;
  if (IDENTITY_KEYS.has(query.sortKey) || rows.length === 0) return null;
  const known = rows.filter((r) => r.values[query.sortKey]?.value != null);
  if (known.length > 0) return null;
  const label = byKey.get(query.sortKey)?.label ?? humaniseComputed(query.sortKey);
  return (
    <p class="sort-empty-note">
      No lens in this view has a {label.toLowerCase()} on record, so the order is
      arbitrary. Nobody has cited one yet.
    </p>
  );
}

const FOCAL_KEYS = new Set(["focal_length_min_mm", "focal_length_max_mm"]);

/**
 * A component that re-renders only when `same` says its props changed.
 *
 * Paging appends to the list (REQ-WEB-030), and a plain render rebuilt every
 * card already on screen each time: thousands of rows' worth of element objects
 * thrown away per page. Past about 3,000 rows that garbage grew the browser's
 * heap to ~1.9 GB, and WebKit then stopped the page for ~2 s to collect it, an
 * append that normally takes 0.16 s (REQ-WEB-018). A row that has not changed
 * has no reason to be rebuilt.
 */
function memoRow<P>(render: (p: P) => ComponentChild, same: (a: P, b: P) => boolean) {
  return class extends Component<P> {
    override shouldComponentUpdate(next: P) {
      return !same(this.props, next);
    }
    override render() {
      return render(this.props);
    }
  };
}

interface CardProps {
  row: LensRow;
  query: Query;
  byKey: Map<string, Attribute>;
  range: Range | undefined;
  specKeys: string[];
  /** Null unless the list shows a focal length at all. */
  focal: string | null;
  /** The mount's name, only when the list spans several (#211). */
  mount: string | undefined;
  crops: boolean;
  ticked: boolean;
  headlineOpen: boolean;
  lensSearch: string | undefined;
  onOpen: (slug: string) => void;
  onToggleSelect: (slug: string) => void;
  onToggleHeadline: (slug: string) => void;
}

/**
 * One result on a phone. Everything a card draws arrives as a prop that is
 * stable while the card is unchanged (the row object itself, plain strings and
 * booleans, callbacks that do not change per render), so memoRow can tell.
 */
const Card = memoRow<CardProps>(
  ({ row, query, byKey, range, specKeys, focal, mount, crops, ticked, headlineOpen,
     lensSearch, onOpen, onToggleSelect, onToggleHeadline }) => {
    const headlineCell = row.values[query.sortKey];
    return (
          <li class={`card-wrap${ticked ? " card-wrap-selected" : ""}`}>
            {/* Outside the anchor deliberately: nesting a control inside a link
                makes every tick a navigation, and is invalid HTML besides. */}
            <label class="pick">
              <input
                type="checkbox"
                checked={ticked}
                onChange={() => onToggleSelect(row.slug)}
              />
              {/* A word under the tick (#213): the box alone did not say what it
                  was for. Hidden from assistive technology, which already
                  hears the full "Compare <lens>" below. */}
              <span class="pick-caption" aria-hidden="true">Compare</span>
              <span class="visually-hidden">
                    Compare {lensName(row.manufacturer_name, row.model_name)}
                  </span>
            </label>
            <a class="card" {...openable(row.slug, onOpen, lensSearch)}>
            <div class="card-top">
              <div class="card-identity">
                <h2 class="card-name">
                  {lensName(row.manufacturer_name, row.model_name)}
                  {crops ? <CropsBadge /> : null}
                </h2>
                {/* REQ-WEB-031: the product code was here and is an internal
                    key. None of the nine sites surveyed shows one; it stays on
                    the detail page, where a contributor needs it. */}
                <p class="card-sub">
                  {mount ? <span class="card-mount">{mount}</span> : null}
                  {/* Its own item, so the line's gap sits evenly either side. */}
                  {mount && focal ? <span class="card-sep" aria-hidden="true">·</span> : null}
                  {focal ? <span class="card-focal">{focal}</span> : null}
                </p>
              </div>
              <Headline
                row={row}
                query={query}
                byKey={byKey}
                open={headlineOpen}
                onOpenProvenance={() => onToggleHeadline(row.slug)}
              />
            </div>

            {headlineOpen && !IDENTITY_KEYS.has(query.sortKey) ? (
              <div class="headline-prov">
                <CellProvenance
                  slug={row.slug}
                  attributeKey={query.sortKey}
                  attr={byKey.get(query.sortKey)}
                  sourceId={headlineCell?.source_id ?? null}
                  competing={headlineCell?.competing_values ?? 0}
                  distinct={headlineCell?.distinct_values ?? headlineCell?.competing_values ?? 0}
                  publishers={headlineCell?.distinct_publishers ?? headlineCell?.competing_values ?? 0}
                  agree={headlineCell?.values_agree}
                  unknown={formatCellWithUnit(byKey.get(query.sortKey), headlineCell) === UNKNOWN}
                  notApplicable={headlineCell?.state === "not_applicable"}
                />
              </div>
            ) : null}

            <Bar
              attr={byKey.get(query.sortKey)}
              cell={row.values[query.sortKey]}
              range={range}
            />

            {specKeys.length ? (
              <dl class="specs">
                {specKeys.map((k) => (
                  <Spec key={k} k={k} attr={byKey.get(k)} cell={row.values[k]}
                        slug={row.slug} />
                ))}
              </dl>
            ) : null}
            </a>
          </li>
    );
  },
  (a, b) =>
    a.row === b.row && a.query === b.query && a.byKey === b.byKey &&
    a.range?.min === b.range?.min && a.range?.max === b.range?.max &&
    a.specKeys === b.specKeys && a.focal === b.focal && a.mount === b.mount &&
    a.crops === b.crops && a.ticked === b.ticked && a.headlineOpen === b.headlineOpen &&
    a.lensSearch === b.lensSearch && a.onOpen === b.onOpen &&
    a.onToggleSelect === b.onToggleSelect && a.onToggleHeadline === b.onToggleHeadline,
);

export function ResultCards({ rows, query, byKey, ranges, onOpen, selected, onToggleSelect, cropsBelow, mountOf, lensSearch }: Props) {
  // One open at a time: several expanded citations turn a scannable list into
  // a wall, and nobody compares two sources by scrolling past twelve.
  const [openHeadline, setOpenHeadline] = useState<string | null>(null);
  const onToggleHeadline = useCallback(
    (slug: string) => setOpenHeadline((cur) => (cur === slug ? null : slug)),
    [],
  );
  // Focal length reads as one fact, so the two stored columns collapse into it.
  // The array is held so an append hands every existing card the one it had.
  const specKeys = useMemo(
    () => query.columns.filter((k) => k !== query.sortKey && !FOCAL_KEYS.has(k)),
    [query.columns, query.sortKey],
  );
  const showFocal = query.columns.some((k) => FOCAL_KEYS.has(k));
  const range = ranges.get(query.sortKey);

  return (
    <>
    <ol class="cards">
      {rows.map((row) => (
        <Card
          key={row.slug}
          row={row}
          query={query}
          byKey={byKey}
          range={range}
          specKeys={specKeys}
          focal={showFocal ? focalSummary(row, byKey) : null}
          mount={mountOf ? mountOf(row) : undefined}
          crops={cropsBelow?.(row) ?? false}
          ticked={selected.has(row.slug)}
          headlineOpen={openHeadline === row.slug}
          lensSearch={lensSearch}
          onOpen={onOpen}
          onToggleSelect={onToggleSelect}
          onToggleHeadline={onToggleHeadline}
        />
      ))}
    </ol>
    <SortEmptyNote rows={rows} query={query} byKey={byKey} />
    </>
  );
}

function TableCell(props: {
  k: string;
  attr: Attribute | undefined;
  cell: Cell | undefined;
  slug: string;
  sorted: boolean;
}) {
  const [open, setOpen] = useState(false);
  const text = formatCell(props.attr, props.cell);
  const unknown = text === UNKNOWN;
  const competing = props.cell?.competing_values ?? 0;
  const distinct = props.cell?.distinct_values ?? competing;
  const publishers = props.cell?.distinct_publishers ?? competing;
  const agree = props.cell?.values_agree;
  const focus = useFocusSource(props.cell?.source_id ?? null, unknown);
  if (hasNoCitation(props.cell)) {
    // No citation to open: the number was derived here, not stated by anyone,
    // or a rule says the spec cannot apply to this lens (REQ-WEB-007). A "not
    // applicable" a source stated has a citation and opens below (D45).
    return (
      <td
        class={`td-num td-computed${unknown ? " td-unknown" : ""}${props.cell?.state === "not_applicable" ? " td-na" : ""}${props.sorted ? " td-sorted" : ""}`}
      >
        {text}
      </td>
    );
  }
  return (
    <td
      class={`td-num${unknown ? " td-unknown" : ""}${props.cell?.state === "not_applicable" ? " td-na" : ""}${props.sorted ? " td-sorted" : ""}`}
    >
      <button type="button" class="cell-button" aria-expanded={open}
              {...focus.focusProps} onClick={() => setOpen(!open)}>
        {text}
        <AgreementMark competing={competing} distinct={distinct} publishers={publishers} agree={agree} />
      </button>
      {focus.captionEl}
      {open ? (
        <div class="cell-prov">
          <CellProvenance slug={props.slug} attributeKey={props.k}
                          attr={props.attr}
                          sourceId={props.cell?.source_id ?? null}
                          competing={competing} distinct={distinct}
                          publishers={publishers} agree={agree} unknown={unknown}
                          notApplicable={props.cell?.state === "not_applicable"} />
        </div>
      ) : null}
    </td>
  );
}

/**
 * The columns the table draws: the chosen ones, and the sorted spec in front of
 * them if it is not already one (D7: the number the list is ordered by is
 * always on screen, as the headline is on a phone card). Without this, sorting
 * by release date on a wide screen ordered the list by a value nobody could
 * see. The API already returns it (`requestColumns`).
 */
function tableColumns(query: Query, byKey: Map<string, Attribute>): string[] {
  const k = query.sortKey;
  if (query.columns.includes(k) || IDENTITY_KEYS.has(k) || !byKey.has(k)) return query.columns;
  return [k, ...query.columns];
}

interface TableRowProps {
  row: LensRow;
  query: Query;
  byKey: Map<string, Attribute>;
  cols: string[];
  mount: string | undefined;
  crops: boolean;
  ticked: boolean;
  lensSearch: string | undefined;
  onOpen: (slug: string) => void;
  onToggleSelect: (slug: string) => void;
}

/** One result on a wide screen; skipped on an append like Card (REQ-WEB-018). */
const TableRow = memoRow<TableRowProps>(
  ({ row, query, byKey, cols, mount, crops, ticked, lensSearch, onOpen, onToggleSelect }) => (
    <tr
      class={`row-openable${ticked ? " row-selected" : ""}`}
      onClick={(e) => openFromRow(e, row.slug, onOpen)}
    >
      <td class="td-pick">
        <label class="pick">
          <input
            type="checkbox"
            checked={ticked}
            onChange={() => onToggleSelect(row.slug)}
          />
          <span class="visually-hidden">
            Compare {lensName(row.manufacturer_name, row.model_name)}
          </span>
        </label>
      </td>
      <th scope="row" class="td-name">
        <a {...openable(row.slug, onOpen, lensSearch)}>
          {lensName(row.manufacturer_name, row.model_name)}
        </a>
        {crops ? <CropsBadge /> : null}
        {mount ? <span class="td-mount">{mount}</span> : null}
      </th>
      {cols.map((k) => (
        <TableCell
          key={k}
          k={k}
          attr={byKey.get(k)}
          cell={row.values[k]}
          slug={row.slug}
          sorted={k === query.sortKey}
        />
      ))}
    </tr>
  ),
  (a, b) =>
    a.row === b.row && a.query === b.query && a.byKey === b.byKey && a.cols === b.cols &&
    a.mount === b.mount && a.crops === b.crops && a.ticked === b.ticked &&
    a.lensSearch === b.lensSearch && a.onOpen === b.onOpen &&
    a.onToggleSelect === b.onToggleSelect,
);

export function ResultTable({ rows, query, byKey, onOpen, selected, onToggleSelect, cropsBelow, mountOf, lensSearch }: Props) {
  // Held so an append hands every existing row the same array it had.
  const cols = useMemo(() => tableColumns(query, byKey), [query, byKey]);
  return (
    <div class="table-scroll">
      <table class="table">
        <thead>
          <tr>
            <th scope="col" class="th-pick">
              Compare
            </th>
            <th scope="col" class="th-name">
              Lens
            </th>
            {cols.map((k) => {
              const { name, unit } = labelParts(byKey.get(k), k);
              return (
                <th key={k} scope="col" class="th-num">
                  {name}
                  {/* The header is drawn in capitals; a unit is not shouted
                      ("Weight (g)", not "WEIGHT (G)") (#213). */}
                  {unit ? <span class="th-unit"> ({unit})</span> : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.slug}
              row={row}
              query={query}
              byKey={byKey}
              cols={cols}
              mount={mountOf ? mountOf(row) : undefined}
              crops={cropsBelow?.(row) ?? false}
              ticked={selected.has(row.slug)}
              lensSearch={lensSearch}
              onOpen={onOpen}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Shown when a query legitimately matches nothing — a state, not an error. */
export function EmptyResult(props: { hasFilters: boolean; onClear: () => void }) {
  return (
    <div class="empty">
      <p class="empty-title">No lens matches all of those filters.</p>
      {props.hasFilters ? (
        <>
          <p class="empty-body">
            Widen one of them, or remove it. Bear in mind a lens with no cited value for a spec
            is left out by a filter on it, so some matching lenses may be missing simply because nobody
            has cited that spec for them yet.
          </p>
          <button type="button" class="button" onClick={props.onClear}>
            Clear all filters
          </button>
        </>
      ) : null}
    </div>
  );
}

/** Ranges are per-attribute; this is only used for the sorted attribute's bar. */
export function rangeSummary(attr: Attribute | undefined, range: Range | undefined): string | null {
  if (!attr || !range || range.min === null || range.max === null) return null;
  const lo = formatNumber(toDisplay(attr, range.min), attr.display_step);
  const hi = formatNumber(toDisplay(attr, range.max), attr.display_step);
  const unit = attr.unit_family !== "none" ? (attr.display_unit ?? "") : "";
  return `${lo}–${hi} ${unit}`.trim();
}
